import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ScrollMenu } from 'react-horizontal-scrolling-menu';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFilePicker } from 'use-file-picker';

import { useQueryParams } from '../../../utils/useQueryParams';
import { resizeToBase64 } from '../../../utils/resizeImage';
import isResizableMimetype from '../../../utils/ImageUtils';
import { isDomainValid } from '../../../utils/validateUrl';
import { getSpotsCategoryLocaleValue } from '../../../utils/spotsHelper';
import { getMemberIds } from '../../../utils/etcUtils';
import { transformDateToInputValue } from '../../../utils/DateUtil';
import { requestEventChange } from '../../../data/repositories/RequestChange';
import AddReportEvent from '../../../components/api/mutations/AddReportEvent';

import StickyHeader from '../../../components/StickyHeader';
import StepComponent from '../../../components/steps/StepComponent';
import { ReportFootprintBreadcrumb } from '../../../components/report/ReportFootprintBreadcrumb';
import HorizontalScroll from '../../../presentation/my-page/components/HorizontalScroll';

import { CheckCircleFill, Xmark } from '@peacecover/icon';
import { Button, InputBoxSingle } from '@peacecover/component';
import { ReactComponent as CameraIcon } from '../../../icons/camera.svg';

export default function AddReportEventPage() {
  const { queryParams, updateQueryParams } = useQueryParams();
  const navigate = useNavigate();

  const { search } = useLocation();
  const currentQueryParams = new URLSearchParams(search);
  const kakaoUID = currentQueryParams.get('kakaoUID');
  const locationName = currentQueryParams.get('locationName');
  const artistId = currentQueryParams.get('artistId');
  const memberId = currentQueryParams.get('memberId');
  const siteType = currentQueryParams.get('spotsCategory');
  const memberName = queryParams.get('memberName') || '';
  const isEdit = currentQueryParams.get('isEdit') === 'true';

  const { event } = useLocation().state || {};
  const locationId = event?._locationId;
  const eventId = event?._eventId;

  const [imageDataUrl, setImageDataUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [selectedStartDate, setSelectedStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedEndDate, setSelectedEndDate] = useState(
    new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().slice(0, 10)
  );
  const [title, setTitle] = useState('');
  const [organizer, setOrganizer] = useState('');
  const [isSelfHosted, setIsSelfHosted] = useState(false);
  const [detailLocation, setDetailLocation] = useState('');
  const [detailImages, setDetailImages] = useState<string[]>([]);
  const [isLinkUrlValid, setIsLinkUrlValid] = useState(true);
  const detailLocationRef = useRef<HTMLTextAreaElement | null>(null);

  const parsedMemberIds = getMemberIds(memberId);

  // 포스터 전용 파일 피커 (단일)
  const {
    openFilePicker: openPosterPicker,
    filesContent: posterFiles,
    plainFiles: posterPlainFiles
  } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: false
  });

  // 상세사진 전용 파일 피커 (다중, 최대 10장)
  const {
    openFilePicker: openDetailPicker,
    filesContent: detailFiles,
    plainFiles: detailPlainFiles
  } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: true
  });

  const handleAddPosterClick = () => {
    openPosterPicker();
  };

  const handleAddDetailImagesClick = () => {
    openDetailPicker();
  };

  // state로 event가 넘어올 경우(수정) 초기값 설정
  useEffect(() => {
    if (event) {
      setSelectedStartDate(transformDateToInputValue(event._startDate.toString()));
      setSelectedEndDate(transformDateToInputValue(event._endDate.toString()));
      setTitle(event._name);
      setOrganizer(event._organizerTwitterId);
      setIsSelfHosted(event._isSelfHosted || false);
      setImageDataUrl(event._baseImageUrl);
      setDetailLocation(event._description);
      setLinkUrl(event._linkUrl);
    }
  }, [event]);

  // 포스터 파일 선택 반영 (리사이즈 적용)
  useEffect(() => {
    if (posterFiles && posterFiles.length > 0) {
      const file = (posterPlainFiles as File[] | undefined)?.[0];
      if (file && isResizableMimetype(file.type)) {
        resizeToBase64(file).then((dataUrl) => {
          setImageDataUrl(dataUrl);
        });
      } else {
        setImageDataUrl(posterFiles[0]?.content as string);
      }
    }
  }, [posterFiles, posterPlainFiles]);

  // 상세사진 파일 선택 반영 (리사이즈 적용, 최대 10장)
  useEffect(() => {
    const run = async () => {
      if (detailFiles && detailFiles.length > 0) {
        const tasks = detailFiles.map((f, idx) => {
          const file = (detailPlainFiles as File[] | undefined)?.[idx];
          if (file && isResizableMimetype(file.type)) {
            return resizeToBase64(file);
          }
          return Promise.resolve((f.content ?? '') as string);
        });
        const newImages = (await Promise.all(tasks)).filter(Boolean) as string[];
        if (newImages.length > 0) {
          setDetailImages((prev) => {
            const merged = [...prev, ...newImages];
            return merged.slice(0, 10);
          });
        }
      }
    };
    run();
  }, [detailFiles, detailPlainFiles]);

  const submitEdit = async () => {
    if (!locationId || !eventId) return;
    setIsUploading(true);
    try {
      const response = await requestEventChange({
        locationId,
        eventId,
        artistId: artistId || '',
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        title,
        organizerTweetId: organizer,
        posterDataUrls: [imageDataUrl, ...detailImages],
        description: detailLocation,
        linkUrl,
        memberIds: parsedMemberIds.length > 0 ? parsedMemberIds : []
      });
      if (response.ok) {
        navigate('/report-location/events/complete-edit');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  const submitCreate = async () => {
    if (!artistId || parsedMemberIds.length === 0 || !locationName || !kakaoUID || !siteType) {
      return;
    }
    setIsUploading(true);
    try {
      const response = await AddReportEvent({
        artistId: artistId,
        memberIds: parsedMemberIds,
        location: {
          name: locationName,
          kakaoUid: kakaoUID,
          siteType: siteType
        },
        event: {
          startDate: selectedStartDate,
          endDate: selectedEndDate,
          name: title,
          organizerTweetId: organizer,
          description: detailLocation,
          posterDataUrls: [imageDataUrl, ...detailImages],
          linkUrl: linkUrl
        }
      });
      if (response.ok) {
        const data = await response.json();
        updateQueryParams(
          { locationId: data.event.locationId, eventId: data.event.eventId },
          '/report-location/events/complete'
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  const onDone = async () => {
    if (!isLinkUrlValid) {
      alert('유효한 링크인지 확인 후 입력해 주세요');
      return;
    }
    if (isUploading) return;
    if (isEdit) {
      await submitEdit();
    } else {
      await submitCreate();
    }
  };

  const spotsCategory = getSpotsCategoryLocaleValue(siteType || '');
  const isBillboardAd = siteType === 'billboard_ad';
  const currentStep = (() => {
    let step = 0;
    if (selectedStartDate && selectedEndDate) step = 1;
    if (title?.trim()) step = 2;
    if (organizer?.trim()) step = 3;
    if (imageDataUrl) step = 4;
    return step;
  })();

  return (
    <>
      <Helmet>
        <title>{t('AddReportEventPage.pageTitle', { defaultValue: '제보하기' })}</title>
      </Helmet>
      <StickyHeader
        title={t('AddReportEventPage.pageTitle', { defaultValue: '제보하기' }) as string}
        alwaysShowTitle={true}
        showBackButton={true}
        showTitle={true}
        onClickBackButton={() => navigate(`/report-location/events?${queryParams.toString()}`)}
      />
      <div className="relative min-h-[100lvh] flex flex-col pt-[calc(54px+env(safe-area-inset-top))] pb-[env(safe-area-inset-bottom)]">
        <div className="px-[24px] py-[10px]">
          <StepComponent currentStep={currentStep} nextStep={1} maxStep={999} title="">
            <div className="flex flex-col gap-[18px]">
              <div className="mx-[-24px]">
                <ReportFootprintBreadcrumb />
              </div>
              <div className="flex flex-col gap-[16px]">
                <div className="text-[26px] font-bold whitespace-pre-wrap leading-tight text-text-main-lv1">
                  {t('AddReportEventPage.title', {
                    spotsCategory: spotsCategory,
                    defaultValue: '생일카페의 자세한 정보를 \n입력해 주세요.'
                  })}
                </div>
                <div className="flex flex-col gap-1">
                  <div className="py-[12px] text-[17px] font-semibold leading-[1] text-text-main-lv2">
                    {t('AddReportEventPage.dateRangeTitle', {
                      defaultValue: '언제부터 언제까지 진행되나요?'
                    })}
                  </div>
                  <div className="flex gap-[6px] pb-4">
                    <input
                      type="date"
                      className="w-[162px] font-semibold text-left h-[54px] px-[16px] border border-separator1 rounded-[12px] text-text-main-lv2 bg-transparent focus:outline-none"
                      value={selectedStartDate}
                      onChange={(e) => setSelectedStartDate(e.target.value)}
                    />
                    <input
                      type="date"
                      className="w-[162px] text-[17px] font-semibold text-left h-[54px] px-[16px] border border-separator1 rounded-[12px] text-text-main-lv2 bg-transparent focus:outline-none"
                      value={selectedEndDate}
                      onChange={(e) => setSelectedEndDate(e.target.value)}
                      min={selectedStartDate}
                    />
                  </div>
                </div>
              </div>
            </div>
          </StepComponent>
          <StepComponent currentStep={currentStep} nextStep={2} maxStep={999} title="">
            <div className="flex flex-col gap-1 mt-[14px]">
              <div className="py-3 text-[17px] font-semibold leading-[1] text-main-light-2 dark:text-main-dark-2">
                {t('AddReportEventPage.nameTitle', {
                  spotsCategory: spotsCategory,
                  defaultValue: '생일카페 제목을 알려주세요.'
                })}
              </div>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('AddReportEventPage.namePlaceholder', {
                  defaultValue: '최애의 {{spotsCategory}}',
                  memberName: memberName || '최애',
                  spotsCategory: spotsCategory
                }).toString()}
                className="w-full bg-transparent px-[16px] py-[17px] rounded-[12px] border border-separator1 text-[17px] leading-[20px] text-text-main-lv2 font-medium placeholder:text-placeholder-light dark:placeholder:text-placeholder-dark focus:outline-none"
              />
            </div>
          </StepComponent>
          <StepComponent currentStep={currentStep} nextStep={3} maxStep={999} title="">
            <div className="flex flex-col gap-1 mt-[14px]">
              <div className="py-3 text-[17px] font-semibold leading-[1] text-main-light-2 dark:text-main-dark-2">
                {t('AddReportEventPage.organizerTitle', {
                  defaultValue: '주최자를 알려주세요.'
                })}
              </div>
              <input
                type="text"
                value={organizer}
                onChange={(e) => setOrganizer(e.target.value)}
                placeholder={t('AddReportEventPage.organizerPlaceholder', {
                  defaultValue: '주최자 이름'
                }).toString()}
                className="w-full bg-transparent px-[16px] py-[17px] rounded-[12px] border border-separator1 text-[17px] leading-[20px] text-text-main-lv2 font-medium placeholder:text-placeholder-light dark:placeholder:text-placeholder-dark focus:outline-none"
              />
            </div>
            <label className="inline-flex mt-2 items-center gap-[6px] cursor-pointer">
              <input
                type="checkbox"
                className="peer hidden"
                checked={isSelfHosted}
                onChange={(e) => setIsSelfHosted(e.target.checked)}
              />
              <CheckCircleFill
                className={'w-[24px] h-[24px]'}
                backgroundColor={isSelfHosted ? 'var(--color-primary)' : 'var(--color-surface-lv2)'}
                strokeColor={isSelfHosted ? 'var(--color-on-primary)' : 'var(--color-text-sub-lv2)'}
              />
              <span className="text-[14px] font-medium text-text-main-lv4">
                {t('AddReportEventPage.selfHosted', {
                  defaultValue: '직접 주최한 경우에는 체크해 주세요.'
                })}
              </span>
            </label>
          </StepComponent>
          <StepComponent currentStep={currentStep} nextStep={4} maxStep={999} title="">
            <div className="flex flex-col gap-1 mt-[14px]">
              <div className="py-3 text-[17px] font-semibold leading-[1] text-main-light-2 dark:text-main-dark-2">
                {t('AddReportEventPage.posterTitle', {
                  defaultValue: '대표 포스터를 첨부해 주세요!'
                })}
                {isBillboardAd && (
                  <p className="text-[14px] mt-[10px] text-text-main-lv4 leading-none">
                    {t('AddReportEventPage.posterDescription', {
                      defaultValue: '썸네일로 보여지는 이미지예요!'
                    })}
                  </p>
                )}
              </div>
            </div>
            <div className="relative">
              <ScrollMenu wrapperClassName="poster-scroll-container">
                <button
                  type="button"
                  className="border border-separator-light-1 dark:border-separator-dark-1 w-[75px] h-[98px] flex flex-col justify-center items-center mt-[12px] rounded-[12px] bg-transparent"
                  onClick={handleAddPosterClick}
                >
                  <CameraIcon className="fill-black dark:fill-white" />
                  <div className="text-[12px] text-sub-light-1 dark:text-sub-dark-1">
                    {imageDataUrl ? 1 : 0}/1
                  </div>
                </button>
                <div className="flex">
                  {imageDataUrl && (
                    <div className="w-[75px] h-[98px] relative ml-2 mt-[12px]">
                      <img
                        src={imageDataUrl}
                        alt={'Attached'}
                        className="w-[75px] h-[98px] rounded-[8px] object-cover max-w-full max-h-full"
                      />
                      <button
                        type="button"
                        className="absolute top-[-10px] right-[-10px] z-10 rounded-full p-[5px] cursor-pointer inline-flex bg-primary-light dark:bg-primary-dark"
                        onClick={() => setImageDataUrl('')}
                      >
                        <Xmark size={14} className="text-on-primary" />
                      </button>
                    </div>
                  )}
                </div>
              </ScrollMenu>
            </div>
          </StepComponent>
          {imageDataUrl && (
            <div className="flex flex-col gap-[4px] mt-[16px]">
              <div className="flex flex-col gap-[8px] py-[12px] leading-none">
                <p className="text-[17px] font-bold text-text-main-lv1">
                  {t('AddReportEventPage.originalPostTitle', {
                    defaultValue: '해당 이벤트 관련 원본 글이 있나요?'
                  })}
                </p>
                <p className="text-[14px] text-text-main-lv4">
                  {t('AddReportEventPage.originalPostDescription', {
                    defaultValue: '이벤트가 언급된 게시물 링크가 있다면 첨부해주세요'
                  })}
                </p>
              </div>
              <div className="pt-[12px] pb-[5px] relative">
                <InputBoxSingle
                  value={linkUrl}
                  onChange={(e) => {
                    setLinkUrl(e.target.value);
                    if (isDomainValid(e.target.value)) {
                      setIsLinkUrlValid(true);
                    } else {
                      setIsLinkUrlValid(false);
                    }
                  }}
                  placeholder={t('AddReportEventPage.originalPostPlaceholder', {
                    defaultValue: '내용'
                  }).toString()}
                  className="bg-transparent"
                  maxLength={200}
                />
                {!isLinkUrlValid && (
                  <p className="text-[11px] text-on-warning absolute bottom-[-15px]">
                    {t('AddReportEventPage.originalPostInvalid', {
                      defaultValue: '유효한 링크인지 확인 후 입력해주세요'
                    })}
                  </p>
                )}
              </div>
            </div>
          )}
          {isBillboardAd && imageDataUrl && (
            <div className="flex flex-col gap-[14px] mt-[14px]">
              <div className="flex flex-col gap-1">
                <div className="py-3 text-[17px] font-semibold leading-[1] text-main-light-2 dark:text-main-dark-2">
                  {t('AddReportEventPage.locationTitle', {
                    defaultValue: '전광판을 볼 수 있는 정확한 위치를 적어주세요!'
                  })}
                </div>
                <textarea
                  ref={detailLocationRef}
                  rows={1}
                  className="w-full px-[16px] py-[17px] rounded-[12px] min-h-[80px] bg-secondary-light dark:bg-secondary-dark border border-separator1 text-[15px] leading-[18px] text-text-main-lv2 font-medium placeholder:text-placeholder-light dark:placeholder:text-placeholder-dark focus:outline-none resize-none overflow-hidden break-words"
                  value={detailLocation}
                  onChange={(e) => {
                    setDetailLocation(e.target.value);
                    const el = detailLocationRef.current;
                    if (el) {
                      el.style.height = 'auto';
                      el.style.height = `${el.scrollHeight}px`;
                    }
                  }}
                  placeholder={t('AddReportEventPage.locationPlaceholder', {
                    defaultValue: 'ex) 2번 출구 앞'
                  }).toString()}
                />
              </div>
              <div className="flex flex-col gap-1">
                <div className="pt-3 text-[17px] font-semibold leading-[1] text-text-main-lv2">
                  {t('AddReportEventPage.detailImagesTitle', {
                    defaultValue: '상세사진이 있다면 추가해주세요.'
                  })}
                </div>
                <HorizontalScroll>
                  <div className="flex gap-[12px] py-[12px]">
                    <button
                      type="button"
                      className="border border-separator-light-1 dark:border-separator-dark-1 w-[75px] h-[75px] flex flex-col justify-center items-center rounded-[12px] bg-transparent"
                      onClick={handleAddDetailImagesClick}
                    >
                      <CameraIcon className="fill-black dark:fill-white" />
                      <div className="text-[12px] text-text-sub-lv1">{detailImages.length}/10</div>
                    </button>
                    <div className="flex gap-[12px]">
                      {detailImages.map((image) => (
                        <div
                          className="w-[75px] h-[75px] relative"
                          key={image + crypto.randomUUID()}
                        >
                          <img
                            src={image}
                            alt={'Attached'}
                            className="w-full h-full rounded-[8px] object-cover max-w-full max-h-full"
                          />
                          <button
                            type="button"
                            className="absolute top-[-10px] right-[-10px] z-10 rounded-full p-[5px] cursor-pointer inline-flex bg-primary-light dark:bg-primary-dark"
                            onClick={() =>
                              setDetailImages((prev) => prev.filter((img) => img !== image))
                            }
                          >
                            <Xmark size={14} className="text-on-primary" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </HorizontalScroll>
              </div>
            </div>
          )}
        </div>

        {imageDataUrl && (
          <div
            className={
              'sticky bottom-0 left-0 right-0 bg-background-light dark:bg-background-dark text-center z-[9999] flex gap-[7px]'
            }
            style={{
              padding: '10px 24px calc(10px + env(safe-area-inset-bottom)) 24px'
            }}
          >
            <Button
              size="large"
              btnColor="primary"
              onClick={onDone}
              className="w-full font-semibold text-[17px]"
              disabled={isUploading}
            >
              {t('done')}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
